import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LifeBuoy, Phone, ExternalLink, HeartHandshake, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { CRISIS_LINES } from "@/components/CrisisResources";

// Shown in-chat when the companion detects a safety signal.
// level: "crisis" (acute risk → emergency lines) or "professional" (gentle nudge to Find Help).
export function SupportCard({ level, onDismiss }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  if (!level) return null;

  const crisis = level === "crisis";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      data-testid={`support-card-${level}`}
      className={`relative mb-3 rounded-2xl border p-4 ${crisis ? "border-destructive/40 bg-destructive/5" : "border-primary/30 bg-primary/5"}`}
    >
      <button onClick={onDismiss} aria-label={t("support.close")} data-testid="support-card-dismiss"
              className="absolute right-3 top-3 text-muted-foreground transition-colors hover:text-foreground">
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${crisis ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
          {crisis ? <LifeBuoy className="h-5 w-5" strokeWidth={1.5} /> : <HeartHandshake className="h-5 w-5" strokeWidth={1.5} />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg">{crisis ? t("support.crisisTitle") : t("support.proTitle")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{crisis ? t("support.crisisBody") : t("support.proBody")}</p>

          {crisis ? (
            <div className="mt-3 space-y-2">
              {CRISIS_LINES.map((l) => (
                <a key={l.name} href={l.url || undefined} target={l.url ? "_blank" : undefined} rel="noreferrer"
                   data-testid="support-crisis-line"
                   className="flex items-center justify-between rounded-xl border border-border/60 bg-card p-3 transition-colors hover:bg-accent">
                  <div>
                    <div className="text-sm font-medium">{l.name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" strokeWidth={1.5} /> {l.contact}
                    </div>
                  </div>
                  {l.url && <ExternalLink className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
                </a>
              ))}
            </div>
          ) : (
            <Button onClick={() => navigate("/app/find-help")} data-testid="support-find-help-btn"
                    className="mt-3 rounded-full" size="sm">
              {t("support.proCta")}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
